import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import {
  Sparkles,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  TrendingUp,
  Database,
  Wrench,
  Server,
  BookOpen,
  Filter,
  Map as MapIcon,
} from 'lucide-react';
import { PlatformStatBar } from '@/components/release-notes/PlatformStatBar';
import { WeeklyCadenceStrip } from '@/components/release-notes/WeeklyCadenceStrip';

// AHP patriotic palette — same canonical values as the Quality cards on
// /hospital/{id}/quality (see HospitalProfile.tsx → toneColorsDark/Light).
// Designing for both light + dark via the existing useTheme hook.
const PALETTE = {
  navy: '#001238',
  navyMid: '#002868',
  red: '#B22234',
  redDeep: '#9A1E2D',
  gold: '#C5A55A',
  goldDeep: '#9A7B33',
  goldSoft: '#F5EBD7',
  cream: '#FAF9F6',
  parchment: '#FBF7EE',
  emerald: '#10B981',
  emeraldDeep: '#0F8A5F',
  sky: '#5B9BD5',
  skyDeep: '#1B4F72',
};

// Per-kind tokens — mirror the Quality verdict palette structure exactly.
// "feature" reads as victory/red, "methodology" as authority/navy,
// "data" as commerce/gold, "fix" as growth/emerald, "infra" as utility/sky.
const KIND_TOKENS_DARK: Record<string, { label: string; pillLabel: string; icon: React.FC<{ className?: string }>; fill: string; stripe: string; soft: string; pillBg: string; pillBd: string; pillTx: string }> = {
  feature:     { label: 'Shipped',     pillLabel: 'NEW',         icon: Sparkles,    fill: '#B22234', stripe: '#9A1E2D', soft: 'rgba(178,34,52,0.12)',   pillBg: 'rgba(178,34,52,0.22)',   pillBd: 'rgba(178,34,52,0.60)',   pillTx: '#FCA5A5' },
  methodology: { label: 'Methodology', pillLabel: 'METHOD',      icon: BookOpen,    fill: '#5B9BD5', stripe: '#3A7BB5', soft: 'rgba(91,155,213,0.12)',  pillBg: 'rgba(91,155,213,0.22)',  pillBd: 'rgba(91,155,213,0.60)',  pillTx: '#BCDDF2' },
  data:        { label: 'Data',        pillLabel: 'DATA',        icon: Database,    fill: '#C5A55A', stripe: '#C5A55A', soft: 'rgba(197,165,90,0.10)',  pillBg: 'rgba(197,165,90,0.20)',  pillBd: 'rgba(197,165,90,0.65)',  pillTx: '#F5EBD7' },
  fix:         { label: 'Fix',         pillLabel: 'FIX',         icon: Wrench,      fill: '#10b981', stripe: '#0e9670', soft: 'rgba(16,185,129,0.10)',  pillBg: 'rgba(16,185,129,0.18)',  pillBd: 'rgba(16,185,129,0.55)',  pillTx: '#86efac' },
  infra:       { label: 'Infrastructure', pillLabel: 'INFRA',    icon: Server,      fill: '#7B8AA0', stripe: '#7B8AA0', soft: 'rgba(123,138,160,0.10)', pillBg: 'rgba(123,138,160,0.18)', pillBd: 'rgba(123,138,160,0.50)', pillTx: '#CBD5E1' },
};

const KIND_TOKENS_LIGHT: Record<string, { label: string; pillLabel: string; icon: React.FC<{ className?: string }>; fill: string; stripe: string; soft: string; pillBg: string; pillBd: string; pillTx: string }> = {
  feature:     { label: 'Shipped',     pillLabel: 'NEW',         icon: Sparkles,    fill: '#B22234', stripe: '#B22234', soft: 'rgba(178,34,52,0.06)',   pillBg: 'rgba(178,34,52,0.12)',   pillBd: 'rgba(178,34,52,0.55)',   pillTx: '#8B1828' },
  methodology: { label: 'Methodology', pillLabel: 'METHOD',      icon: BookOpen,    fill: '#1B4F72', stripe: '#1B4F72', soft: 'rgba(27,79,114,0.06)',   pillBg: 'rgba(27,79,114,0.10)',   pillBd: 'rgba(27,79,114,0.40)',   pillTx: '#0E2C44' },
  data:        { label: 'Data',        pillLabel: 'DATA',        icon: Database,    fill: '#9A7B33', stripe: '#C5A55A', soft: 'rgba(197,165,90,0.10)',  pillBg: 'rgba(197,165,90,0.18)',  pillBd: 'rgba(197,165,90,0.65)',  pillTx: '#7A5F1F' },
  fix:         { label: 'Fix',         pillLabel: 'FIX',         icon: Wrench,      fill: '#0F8A5F', stripe: '#0F8A5F', soft: 'rgba(15,138,95,0.06)',   pillBg: 'rgba(15,138,95,0.12)',   pillBd: 'rgba(15,138,95,0.45)',   pillTx: '#0F6B49' },
  infra:       { label: 'Infrastructure', pillLabel: 'INFRA',    icon: Server,      fill: '#475569', stripe: '#64748B', soft: 'rgba(100,116,139,0.06)', pillBg: 'rgba(100,116,139,0.12)', pillBd: 'rgba(100,116,139,0.40)', pillTx: '#334155' },
};

// Contributor identity — color-coded chips. Human contributors map to a
// generic "Team" label by design; agent personas get distinct hues so
// the avatar band reads as a team without naming individuals publicly.
// NOTE on the master label:
//   - Schema field `release_notes.contributor` stays as the literal string
//     'master' (matches the in-system register Ciel uses internally; the
//     rest of the project also keys on this string).
//   - The PUBLIC label rendered to readers is intentionally generic.
//     Personal names do not appear on the public release-notes surface.
const CONTRIBUTOR_TOKENS: Record<string, { label: string; bg: string; tx: string }> = {
  master:      { label: 'Team',       bg: PALETTE.navy,    tx: PALETTE.cream },
  ciel:        { label: 'Ciel',       bg: PALETTE.gold,    tx: PALETTE.navy  },
  'amh-dev':   { label: 'amh-dev',    bg: PALETTE.red,     tx: PALETTE.cream },
  'amh-merch': { label: 'amh-merch',  bg: '#7C3AED',       tx: PALETTE.cream },
  'amh-intl':  { label: 'amh-intl',   bg: '#0891B2',       tx: PALETTE.cream },
  'amh-mktg':  { label: 'amh-mktg',   bg: '#D97706',       tx: PALETTE.cream },
  'amh-ingest':{ label: 'amh-ingest', bg: PALETTE.emerald, tx: PALETTE.cream },
  'amh-audit': { label: 'amh-audit',  bg: '#0F766E',       tx: PALETTE.cream },
};

interface ReleaseNote {
  id: string;
  shipped_at: string;
  kind: 'feature' | 'methodology' | 'data' | 'fix' | 'infra';
  title: string;
  body_md: string | null;
  contributor: string | null;
  links: Array<{ label: string; url: string; kind?: 'video' | 'link'; poster?: string }>;
  metric: Record<string, any> | null;
}

const KINDS: ReleaseNote['kind'][] = ['feature', 'methodology', 'data', 'fix', 'infra'];

// Roadmap item shape — mirrors roadmap_items table 1:1.
interface RoadmapItem {
  id: string;
  title: string;
  description: string | null;
  status: 'planning' | 'in_progress' | 'shipping_soon' | 'paused';
  category: ReleaseNote['kind'];
  timeline: string | null;
  links: Array<{ label: string; url: string; kind?: 'video' | 'link'; poster?: string }>;
  sort_order: number;
}

// Status pill tokens — distinct from category (which uses KIND_TOKENS). Status
// reads as "where are we in the lifecycle", category reads as "what kind of
// work is it". Both pills appear on every roadmap card.
const STATUS_TOKENS: Record<RoadmapItem['status'], { label: string; bg: string; bd: string; tx: string; dotColor: string }> = {
  shipping_soon: { label: 'Shipping soon', bg: 'rgba(178,34,52,0.20)',   bd: 'rgba(178,34,52,0.55)',   tx: '#FCA5A5', dotColor: '#B22234' },
  in_progress:   { label: 'In progress',   bg: 'rgba(197,165,90,0.20)',  bd: 'rgba(197,165,90,0.55)',  tx: '#F5EBD7', dotColor: '#C5A55A' },
  planning:      { label: 'Planning',      bg: 'rgba(91,155,213,0.18)',  bd: 'rgba(91,155,213,0.50)',  tx: '#BCDDF2', dotColor: '#5B9BD5' },
  paused:        { label: 'Paused',        bg: 'rgba(245,235,215,0.10)', bd: 'rgba(245,235,215,0.30)', tx: 'rgba(245,235,215,0.70)', dotColor: 'rgba(245,235,215,0.50)' },
};
const STATUS_TOKENS_LIGHT: typeof STATUS_TOKENS = {
  shipping_soon: { label: 'Shipping soon', bg: 'rgba(178,34,52,0.10)',  bd: 'rgba(178,34,52,0.45)',  tx: '#9A1E2D', dotColor: '#B22234' },
  in_progress:   { label: 'In progress',   bg: 'rgba(197,165,90,0.14)', bd: 'rgba(197,165,90,0.55)', tx: '#7A5F1F', dotColor: '#9A7B33' },
  planning:      { label: 'Planning',      bg: 'rgba(91,155,213,0.10)', bd: 'rgba(91,155,213,0.45)', tx: '#1B4F72', dotColor: '#3A7BB5' },
  paused:        { label: 'Paused',        bg: 'rgba(0,18,56,0.05)',    bd: 'rgba(0,18,56,0.18)',    tx: 'rgba(0,18,56,0.55)', dotColor: 'rgba(0,18,56,0.40)' },
};

function getMonthKey(iso: string) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonthLabel(monthKey: string) {
  const [y, m] = monthKey.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function formatDayHeading(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}

function formatTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function getDayKey(iso: string) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Calendar-cell-friendly number compactor — fits a number into 4 chars.
// 12_345_678 → "12M", 12_345 → "12K", 234 → "234"
function compactNum(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${Math.round(n / 1_000_000)}M`;
  if (n >= 10_000) return `${Math.round(n / 1_000)}K`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

// Inline video for a release note: a `links` entry with kind:"video" (mp4 on
// R2 + optional poster) renders as a player above the body instead of as a
// text link. Screen recordings are made with Playwright + ffmpeg
// (see memory: project_ahp_edition_chargemaster_archive).
function ReleaseVideo({ src, poster, label }: { src: string; poster?: string; label?: string }) {
  return (
    <figure className="my-3 rounded-lg overflow-hidden border" style={{ borderColor: 'rgba(197,165,90,0.35)', background: '#0B1B3F' }}>
      <video controls playsInline preload="metadata" poster={poster} style={{ width: '100%', display: 'block', aspectRatio: '16 / 10' }} aria-label={label || 'Feature walkthrough'}>
        <source src={src} type="video/mp4" />
      </video>
      {label && <figcaption className="px-3 py-1.5 text-[11px]" style={{ color: 'rgba(240,235,227,0.75)' }}>{label}</figcaption>}
    </figure>
  );
}

const isVideoLink = (l: { url: string; kind?: string }) => l.kind === 'video' || /\.mp4(\?|$)/i.test(l.url);

// Minimal markdown renderer for release_notes body_md.
//
// Avoiding a `react-markdown` dependency for what's a tiny vocabulary:
//   **bold**             → <strong>
//   _italic_ / *italic*  → <em>
//   `code`               → <code>
//   [text](https://...)  → <a target="_blank" rel="noopener">
//   blank line           → paragraph break
//   "- " / "* " line     → bullet list item
//
// Returns an array of React elements; safe to embed in JSX. No raw HTML is
// injected (everything is React-rendered), so this is sanitization-clean by
// construction.
function renderInlineMarkdown(text: string): React.ReactNode[] {
  // Split on the union of inline markers in one pass. Each token alternates
  // between literal text and a captured marker; the regex's lookbehind /
  // lookahead emulation keeps `**` from greedily eating `*` boundaries.
  const re = /(\[[^\]]+\]\(https?:\/\/[^)]+\))|(`[^`\n]+`)|(\*\*[^*\n]+\*\*)|(\*[^*\n]+\*)|(_[^_\n]+_)/g;
  const out: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('[')) {
      const linkMatch = /^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/.exec(tok);
      if (linkMatch) {
        out.push(
          <a
            key={`md-${key++}`}
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            {linkMatch[1]}
          </a>
        );
      } else {
        out.push(tok);
      }
    } else if (tok.startsWith('`')) {
      out.push(
        <code
          key={`md-${key++}`}
          style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: '0.88em',
            padding: '1px 5px',
            borderRadius: 3,
            background: 'rgba(127,127,127,0.18)',
          }}
        >
          {tok.slice(1, -1)}
        </code>
      );
    } else if (tok.startsWith('**')) {
      out.push(<strong key={`md-${key++}`}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('*') || tok.startsWith('_')) {
      out.push(<em key={`md-${key++}`}>{tok.slice(1, -1)}</em>);
    } else {
      out.push(tok);
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// Block-level rendering: split body_md into paragraphs + bullet groups,
// then run each line through renderInlineMarkdown for the inline marks.
function MarkdownBody({ text, color }: { text: string; color: string }) {
  // Split into paragraph blocks on blank lines.
  const blocks = text.split(/\n\s*\n/);
  return (
    <div className="text-sm leading-relaxed mb-3" style={{ color }}>
      {blocks.map((block, bi) => {
        const lines = block.split('\n');
        const isLast = bi === blocks.length - 1;
        // ## subheader — single-line block starting with "## "
        if (lines.length === 1 && /^##\s+/.test(lines[0])) {
          return (
            <h4
              key={bi}
              style={{
                fontWeight: 700,
                fontSize: '0.95rem',
                marginTop: bi > 0 ? 16 : 0,
                marginBottom: 6,
                color: 'inherit',
              }}
            >
              {renderInlineMarkdown(lines[0].replace(/^##\s+/, ''))}
            </h4>
          );
        }
        // ### sub-subheader
        if (lines.length === 1 && /^###\s+/.test(lines[0])) {
          return (
            <h5
              key={bi}
              style={{
                fontWeight: 600,
                fontSize: '0.88rem',
                marginTop: bi > 0 ? 12 : 0,
                marginBottom: 4,
                opacity: 0.95,
                color: 'inherit',
              }}
            >
              {renderInlineMarkdown(lines[0].replace(/^###\s+/, ''))}
            </h5>
          );
        }
        // Bullet list: every line in the block starts with "- " or "* "
        const isList = lines.every((l) => /^\s*[-*]\s+/.test(l));
        if (isList) {
          return (
            <ul key={bi} style={{ marginLeft: 18, listStyle: 'disc', marginBottom: isLast ? 0 : 10 }}>
              {lines.map((l, li) => (
                <li key={li} style={{ marginBottom: 3 }}>
                  {renderInlineMarkdown(l.replace(/^\s*[-*]\s+/, ''))}
                </li>
              ))}
            </ul>
          );
        }
        // Default: paragraph. Single newlines within a block render as spaces
        // (CommonMark default for soft-wraps inside a paragraph).
        return (
          <p key={bi} style={{ marginBottom: isLast ? 0 : 10 }}>
            {renderInlineMarkdown(lines.join(' '))}
          </p>
        );
      })}
    </div>
  );
}

const ReleaseNotes: React.FC = () => {
  const brand = useBrand();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const KIND_TOKENS = isDark ? KIND_TOKENS_DARK : KIND_TOKENS_LIGHT;
  const [activeKinds, setActiveKinds] = useState<Set<ReleaseNote['kind']>>(new Set());
  const [activeContributor, setActiveContributor] = useState<string | null>(null);
  const [view, setView] = useState<'timeline' | 'calendar'>('timeline');
  const [monthOffset, setMonthOffset] = useState(0);

  const { data: items, isLoading } = useQuery({
    queryKey: ['release-notes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('release_notes')
        .select('id, shipped_at, kind, title, body_md, contributor, links, metric')
        .eq('visibility', 'public')
        .eq('project_slug', 'americanhealthprices')
        .order('shipped_at', { ascending: false })
        .limit(2000);
      if (error) throw error;
      return (data ?? []) as ReleaseNote[];
    },
  });

  // Hero OG image — most-recent approved primary release-notes screenshot. If
  // an admin has approved one, it replaces the brand-default og:image so the
  // first share preview shows the latest shipped feature rather than the logo.
  const { data: heroOgImage } = useQuery({
    queryKey: ['release-hero-og'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('release_notes_screenshots')
        .select('storage_url, captured_at')
        .eq('status', 'approved')
        .eq('is_primary', true)
        .order('captured_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) return null;
      return data?.storage_url ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Roadmap — public-facing list of active + planned initiatives. Read-only
  // from roadmap_items table; admins manage it via the admin panel. Ordered
  // by sort_order DESC then created_at DESC so curators control ranking.
  const { data: roadmap } = useQuery({
    queryKey: ['roadmap-items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('roadmap_items')
        .select('id, title, description, status, category, timeline, links, sort_order')
        .eq('visibility', 'public')
        .eq('project_slug', 'americanhealthprices')
        .order('sort_order', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as RoadmapItem[];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Daily metrics — fetched only when calendar view is active. Each row carries
  // the four production-metric counts for that day; the calendar render uses
  // them to draw a tiny ingest-pulse footer per cell.
  const { data: dailyMetrics } = useQuery({
    queryKey: ['platform-metrics-daily'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_metrics_daily')
        .select('day, chargemaster_mappings_added, scenarios_added, translations_added, crosswalk_added, voice_lines_added')
        .order('day', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Array<{ day: string; chargemaster_mappings_added: number; scenarios_added: number; translations_added: number; crosswalk_added: number; voice_lines_added: number; }>;
    },
    enabled: view === 'calendar',
    staleTime: 5 * 60 * 1000,
  });

  // Index daily metrics by day-key for O(1) lookup during cell render.
  const dailyByDay = useMemo(() => {
    const m = new Map<string, { chargemaster: number; scenarios: number; translations: number; crosswalk: number; voice: number; total: number }>();
    for (const row of dailyMetrics ?? []) {
      m.set(row.day, {
        chargemaster: row.chargemaster_mappings_added || 0,
        scenarios: row.scenarios_added || 0,
        translations: row.translations_added || 0,
        crosswalk: row.crosswalk_added || 0,
        voice: row.voice_lines_added || 0,
        total: (row.chargemaster_mappings_added || 0) + (row.scenarios_added || 0) + (row.translations_added || 0) + (row.crosswalk_added || 0) + (row.voice_lines_added || 0),
      });
    }
    return m;
  }, [dailyMetrics]);

  // Theme-dependent surface tokens — verbatim from the Quality card system
  // so the page sits inside the same visual family.
  const t = isDark ? {
    pageBg: '#070b1a',
    cardBg: 'linear-gradient(180deg, rgba(2, 22, 60, 0.92) 0%, rgba(0, 14, 44, 0.96) 100%)',
    cardBorder: 'rgba(197, 165, 90, 0.18)',
    cardShadow: '0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 20px -14px rgba(0,0,0,0.6)',
    cardHover: '0 0 0 1px rgba(197,165,90,0.45), 0 12px 30px -14px rgba(0,0,0,0.8)',
    headingText: PALETTE.cream,
    bodyText: 'rgba(245, 235, 215, 0.78)',
    mutedText: 'rgba(245, 235, 215, 0.55)',
    fadedText: 'rgba(245, 235, 215, 0.40)',
    divider: 'rgba(197, 165, 90, 0.18)',
    pillBg: 'rgba(245,235,215,0.06)',
    pillBd: 'rgba(245,235,215,0.18)',
    pillTx: PALETTE.cream,
    statTile: 'linear-gradient(180deg, rgba(2, 22, 60, 0.85) 0%, rgba(0, 14, 44, 0.95) 100%)',
    rail: 'rgba(197, 165, 90, 0.18)',
    railNode: 'rgba(245,235,215,0.85)',
    monthBg: 'rgba(2,22,60,0.5)',
  } : {
    pageBg: PALETTE.parchment,
    cardBg: 'linear-gradient(180deg, #FFFFFF 0%, #FBF7EE 100%)',
    cardBorder: 'rgba(0, 18, 56, 0.12)',
    cardShadow: '0 1px 0 rgba(255,255,255,0.6) inset, 0 4px 14px -6px rgba(0, 18, 56, 0.10)',
    cardHover: '0 0 0 1px rgba(197,165,90,0.50), 0 12px 28px -10px rgba(0,18,56,0.18)',
    headingText: PALETTE.navy,
    bodyText: 'rgba(0, 18, 56, 0.78)',
    mutedText: 'rgba(0, 18, 56, 0.60)',
    fadedText: 'rgba(0, 18, 56, 0.40)',
    divider: 'rgba(197, 165, 90, 0.30)',
    pillBg: 'rgba(0,18,56,0.04)',
    pillBd: 'rgba(0,18,56,0.12)',
    pillTx: PALETTE.navy,
    statTile: 'linear-gradient(180deg, #FFFFFF 0%, #FBF7EE 100%)',
    rail: 'rgba(0, 18, 56, 0.12)',
    railNode: PALETTE.navy,
    monthBg: 'rgba(255,255,255,0.85)',
  };

  // Filter pipeline — kinds AND contributor
  const filtered = useMemo(() => {
    return (items ?? []).filter(it => {
      if (activeKinds.size > 0 && !activeKinds.has(it.kind)) return false;
      if (activeContributor && it.contributor !== activeContributor) return false;
      return true;
    });
  }, [items, activeKinds, activeContributor]);

  // Timeline groups: by day, then chronologically within day
  const timelineByDay = useMemo(() => {
    const map = new Map<string, ReleaseNote[]>();
    for (const it of filtered) {
      const k = getDayKey(it.shipped_at);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(it);
    }
    // Order each day's items oldest-first within day so they read top-to-bottom
    return Array.from(map.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([day, items]) => ({
        day,
        items: items.slice().sort((a, b) => a.shipped_at.localeCompare(b.shipped_at)),
      }));
  }, [filtered]);

  // Header stats
  const stats = useMemo(() => {
    const all = items ?? [];
    if (all.length === 0) return null;
    const sorted = all.slice().sort((a, b) => a.shipped_at.localeCompare(b.shipped_at));
    const first = new Date(sorted[0].shipped_at);
    const last = new Date(sorted[sorted.length - 1].shipped_at);
    const last30 = new Date(Date.now() - 30 * 86400000);
    const last30Items = all.filter(it => new Date(it.shipped_at) >= last30);
    const byKind: Record<string, number> = {};
    for (const it of all) byKind[it.kind] = (byKind[it.kind] ?? 0) + 1;
    let hospitalsTouched = 0, rowsCleaned = 0;
    for (const it of all) {
      if (typeof it.metric?.hospitals_touched === 'number') hospitalsTouched += it.metric.hospitals_touched;
      if (typeof it.metric?.rows_cleaned === 'number') rowsCleaned += it.metric.rows_cleaned;
    }
    return {
      total: all.length,
      last30: last30Items.length,
      first, last,
      byKind,
      hospitalsTouched,
      rowsCleaned,
    };
  }, [items]);

  // Contributor counts for the avatar band
  const contributors = useMemo(() => {
    const counts = new Map<string, number>();
    for (const it of items ?? []) {
      if (!it.contributor) continue;
      counts.set(it.contributor, (counts.get(it.contributor) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .filter(([k]) => CONTRIBUTOR_TOKENS[k])
      .sort((a, b) => b[1] - a[1]);
  }, [items]);

  // Calendar (only computed when calendar view is active to skip work)
  const calendar = useMemo(() => {
    if (view !== 'calendar') return null;
    const byMonth = new Map<string, ReleaseNote[]>();
    for (const it of filtered) {
      const k = getMonthKey(it.shipped_at);
      if (!byMonth.has(k)) byMonth.set(k, []);
      byMonth.get(k)!.push(it);
    }
    const monthsSorted = Array.from(byMonth.keys()).sort().reverse();
    const monthKey = monthsSorted[Math.abs(monthOffset)] ?? monthsSorted[0];
    if (!monthKey) return { cells: [] as Array<{ date: Date | null; items: ReleaseNote[] }>, monthLabel: '', monthsCount: monthsSorted.length };
    const monthItems = byMonth.get(monthKey) ?? [];
    const itemsByDay = new Map<string, ReleaseNote[]>();
    for (const it of monthItems) {
      const k = getDayKey(it.shipped_at);
      if (!itemsByDay.has(k)) itemsByDay.set(k, []);
      itemsByDay.get(k)!.push(it);
    }
    const [y, m] = monthKey.split('-').map(Number);
    const firstOfMonth = new Date(y, m - 1, 1);
    const lastOfMonth = new Date(y, m, 0);
    const startDay = (firstOfMonth.getDay() + 6) % 7;
    const cells: Array<{ date: Date | null; items: ReleaseNote[] }> = [];
    for (let i = 0; i < startDay; i++) cells.push({ date: null, items: [] });
    for (let d = 1; d <= lastOfMonth.getDate(); d++) {
      const date = new Date(y, m - 1, d);
      const k = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({ date, items: itemsByDay.get(k) ?? [] });
    }
    while (cells.length % 7 !== 0) cells.push({ date: null, items: [] });
    return { cells, monthLabel: formatMonthLabel(monthKey), monthsCount: monthsSorted.length };
  }, [view, filtered, monthOffset]);

  return (
    <div style={{ background: t.pageBg, minHeight: '100vh', position: 'relative' }}>
      <Helmet>
        <title>Release Notes — Everything {brand.name} Shipped | Beta</title>
        <meta name="description" content={`Public ship log for ${brand.name}. Features, methodology changes, dataset milestones, and roadmap. Updated as we ship.`} />
        <meta name="robots" content="index,follow" />
        {/* Open Graph + Twitter — uses the latest approved screenshot from
            release_notes_screenshots (admin-curated) when available, otherwise
            falls back to the brand default. Per-release permalink share images
            are wired separately on /release-notes/r/:id. */}
        <meta property="og:title" content={`Release Notes — ${brand.name}`} />
        <meta property="og:description" content={`Everything we shipped, in chronological order. Features + methodology + data milestones + roadmap.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${brand.primaryDomain}/release-notes`} />
        <meta property="og:image" content={heroOgImage || brand.social.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={brand.social.twitter} />
        <meta name="twitter:title" content={`Release Notes — ${brand.name}`} />
        <meta name="twitter:image" content={heroOgImage || brand.social.ogImage} />
      </Helmet>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-14">

        {/* ─── HEADER BAND ─────────────────────────────────────────────── */}
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider"
              style={{ background: PALETTE.gold, color: PALETTE.navy }}
            >
              <Sparkles className="w-3 h-3" /> Public Beta
            </span>
            <span className="text-xs uppercase tracking-widest" style={{ color: t.fadedText }}>
              Release notes
            </span>
          </div>

          <h1
            className="text-4xl sm:text-6xl font-bold tracking-tight mb-3 leading-[1.05]"
            style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, color: t.headingText }}
          >
            Everything <span style={{ color: PALETTE.red }}>{brand.name}</span> shipped.
          </h1>
          <p className="text-base sm:text-lg max-w-2xl mb-8" style={{ color: t.bodyText, lineHeight: 1.5 }}>
            We're in <strong>public beta</strong>. Methodology evolves as the dataset grows; every change ships
            here with reasoning so you can audit the trade-offs. Nothing about your healthcare cost data is final
            because nothing about hospital pricing is final — it's an ongoing investigation, and this page is the
            paper trail.
          </p>

          {/* PLATFORM STAT-BAR — live cumulative totals from platform_stats table.
              Each stat deep-links to its detail surface (e.g., pricing-rows →
              /ingestion-dashboard, scenarios → /scenarios). Replaces the older
              release-notes-derived stat tiles, which counted metadata about THIS
              page's own log rather than platform scale. */}
          <PlatformStatBar isDark={isDark} />

          {/* RELEASE-NOTE METADATA — shrunk to a single line (was 4 stat tiles).
              Still useful for orienting "how many ships in this log" but no
              longer dominates the header. */}
          {stats && (
            <div className="text-xs mb-6" style={{ color: t.fadedText }}>
              <strong style={{ color: t.headingText }}>{stats.total.toLocaleString()}</strong> changes logged since {stats.first.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
              {' · '}
              <strong style={{ color: t.headingText }}>{stats.last30.toLocaleString()}</strong> in the last 30 days
            </div>
          )}

          {/* CONTRIBUTOR ROSTER */}
          {contributors.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap" role="navigation" aria-label="Filter by contributor">
              <span className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: t.fadedText }}>By:</span>
              <button
                type="button"
                onClick={() => setActiveContributor(null)}
                className="text-[11px] px-2 py-1 rounded-full transition-colors"
                style={{
                  background: activeContributor === null ? PALETTE.navy : t.pillBg,
                  color: activeContributor === null ? PALETTE.cream : t.pillTx,
                  border: `1px solid ${activeContributor === null ? PALETTE.navy : t.pillBd}`,
                }}
              >
                All ({stats?.total ?? 0})
              </button>
              {contributors.map(([key, count]) => {
                const tok = CONTRIBUTOR_TOKENS[key]!;
                const active = activeContributor === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveContributor(active ? null : key)}
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-medium transition-all hover:scale-[1.03]"
                    style={{
                      background: tok.bg,
                      color: tok.tx,
                      opacity: active || activeContributor === null ? 1 : 0.5,
                      boxShadow: active ? `0 0 0 2px ${PALETTE.gold}` : 'none',
                    }}
                  >
                    {tok.label}
                    <span style={{ opacity: 0.7 }}>{count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </header>

        {/* ─── STICKY FILTER BAR ─────────────────────────────────────── */}
        <div
          className="sticky top-0 z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 mb-8 backdrop-blur"
          style={{
            background: isDark ? 'rgba(7,11,26,0.85)' : 'rgba(251,247,238,0.85)',
            borderBottom: `1px solid ${t.divider}`,
          }}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4" style={{ color: t.fadedText }} />
            <span className="text-[11px] uppercase tracking-widest font-semibold mr-1" style={{ color: t.fadedText }}>Filter:</span>
            {KINDS.map(k => {
              const tok = KIND_TOKENS[k];
              const active = activeKinds.has(k);
              const Icon = tok.icon;
              const kindCount = stats?.byKind[k] ?? 0;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => {
                    const next = new Set(activeKinds);
                    if (next.has(k)) next.delete(k); else next.add(k);
                    setActiveKinds(next);
                  }}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider transition-all"
                  style={{
                    background: active ? tok.pillBg : t.pillBg,
                    color: active ? tok.pillTx : t.mutedText,
                    border: `1px solid ${active ? tok.pillBd : t.pillBd}`,
                  }}
                >
                  <Icon className="w-3 h-3" />
                  {tok.label}
                  <span style={{ opacity: 0.7, fontWeight: 500 }}>{kindCount}</span>
                </button>
              );
            })}
            <span className="flex-1" />
            {/* View toggle */}
            <div className="flex items-center rounded-md border" style={{ borderColor: t.pillBd }}>
              <button
                type="button"
                onClick={() => setView('timeline')}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider"
                style={{
                  background: view === 'timeline' ? PALETTE.navy : 'transparent',
                  color: view === 'timeline' ? PALETTE.cream : t.mutedText,
                  borderRight: `1px solid ${t.pillBd}`,
                }}
              >
                <TrendingUp className="w-3 h-3" /> Timeline
              </button>
              <button
                type="button"
                onClick={() => setView('calendar')}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider"
                style={{
                  background: view === 'calendar' ? PALETTE.navy : 'transparent',
                  color: view === 'calendar' ? PALETTE.cream : t.mutedText,
                }}
              >
                <CalendarIcon className="w-3 h-3" /> Calendar
              </button>
            </div>
          </div>
        </div>

        {/* ─── WEEKLY PRODUCTION CADENCE ──────────────────────────────
            Sparkline-style strip showing per-week activity across the four
            production metrics (chargemaster pricing rows, scenarios,
            cross-mapped codes, translations). Each row deep-links to its
            detail surface. Pulse table is collapsed by default. */}
        <WeeklyCadenceStrip isDark={isDark} />

        {/* ─── ROADMAP — placed above the shipped log so visitors see
            what's coming before scrolling through the historical timeline. */}
        <section
          id="roadmap"
          className="mt-6 rounded-lg p-6"
          style={{ background: t.cardBg, border: `1px solid ${t.cardBorder}`, boxShadow: t.cardShadow }}
        >
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <div className="flex items-center gap-2">
              <MapIcon className="w-4 h-4" style={{ color: PALETTE.gold }} />
              <h3 className="text-xl font-semibold" style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, color: t.headingText }}>
                Roadmap
              </h3>
            </div>
            <a href="#timeline" className="text-[11px] uppercase tracking-widest underline" style={{ color: PALETTE.gold }}>
              Jump to shipped log ↓
            </a>
          </div>
          <p className="text-sm mb-5" style={{ color: t.bodyText }}>
            Active initiatives and what's coming next. Methodology refinements get queued through the same review
            process as features — read{' '}
            <Link to="/data-methodology" className="underline" style={{ color: PALETTE.gold }}>data methodology</Link>{' '}
            for the current rules.
          </p>

          {!roadmap ? (
            <div className="text-sm italic" style={{ color: t.fadedText }}>Loading initiatives…</div>
          ) : roadmap.length === 0 ? (
            <div className="text-sm italic" style={{ color: t.fadedText }}>No public initiatives queued right now.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {roadmap.map(item => {
                const stTok = (isDark ? STATUS_TOKENS : STATUS_TOKENS_LIGHT)[item.status];
                const catTok = KIND_TOKENS[item.category];
                return (
                  <article
                    key={item.id}
                    className="relative rounded-lg p-4 pl-5"
                    style={{
                      background: t.statTile,
                      border: `1px solid ${t.cardBorder}`,
                      boxShadow: t.cardShadow,
                    }}
                  >
                    {/* Status-coded left rail */}
                    <div
                      className="absolute top-3 bottom-3 left-0 w-[3px] rounded-full"
                      style={{ background: stTok.dotColor, boxShadow: `0 0 8px ${stTok.dotColor}66` }}
                      aria-hidden
                    />
                    <div className="flex items-center gap-1.5 flex-wrap mb-2">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                        style={{ background: stTok.bg, color: stTok.tx, border: `1px solid ${stTok.bd}` }}
                      >
                        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: stTok.dotColor }} aria-hidden />
                        {stTok.label}
                      </span>
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                        style={{ background: catTok.pillBg, color: catTok.pillTx, border: `1px solid ${catTok.pillBd}` }}
                      >
                        <catTok.icon className="w-2.5 h-2.5" /> {catTok.pillLabel}
                      </span>
                      {item.timeline && (
                        <span className="text-[10px] tabular-nums" style={{ color: t.fadedText }}>
                          {item.timeline}
                        </span>
                      )}
                    </div>
                    <h4
                      className="text-sm sm:text-base font-semibold leading-snug mb-1.5"
                      style={{ color: t.headingText }}
                    >
                      {item.title}
                    </h4>
                    {item.links?.filter(isVideoLink).map((l, i) => (
                      <ReleaseVideo key={`v${i}`} src={l.url} poster={l.poster} label={l.label} />
                    ))}
                    {item.description && (
                      <p className="text-[13px] leading-relaxed" style={{ color: t.bodyText }}>
                        {item.description}
                      </p>
                    )}
                    {item.links?.filter(l => !isVideoLink(l)).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {item.links.filter(l => !isVideoLink(l)).map((l, i) => (
                          <a
                            key={i}
                            href={l.url}
                            className="inline-flex items-center gap-1 text-[11px] underline"
                            style={{ color: PALETTE.gold }}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {l.label} <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        ))}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>

        {/* ─── MAIN CONTENT ──────────────────────────────────────────── */}
        <div id="timeline" className="mt-16" />


        {isLoading && (
          <div className="text-center py-16 text-sm" style={{ color: t.mutedText }}>Loading the ship log…</div>
        )}

        {!isLoading && filtered.length === 0 && (
          <div className="text-center py-16">
            <div className="text-base mb-2" style={{ color: t.headingText }}>Nothing matches that filter.</div>
            <button
              type="button"
              onClick={() => { setActiveKinds(new Set()); setActiveContributor(null); }}
              className="text-sm underline"
              style={{ color: PALETTE.gold }}
            >
              Reset filters
            </button>
          </div>
        )}

        {/* TIMELINE VIEW */}
        {view === 'timeline' && filtered.length > 0 && (
          <div className="relative">
            {/* Vertical rail running through the day stack */}
            <div
              className="absolute top-0 bottom-0 w-px hidden sm:block"
              style={{ left: 18, background: `linear-gradient(180deg, ${t.rail} 0%, transparent 100%)` }}
              aria-hidden
            />
            <div className="space-y-10">
              {timelineByDay.map(({ day, items }) => {
                const heading = formatDayHeading(items[0].shipped_at);
                return (
                  <section key={day} className="relative">
                    {/* Day node + heading */}
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className="hidden sm:flex w-9 h-9 rounded-full items-center justify-center text-[11px] font-bold flex-shrink-0"
                        style={{
                          background: t.pageBg,
                          border: `2px solid ${PALETTE.gold}`,
                          color: t.headingText,
                          boxShadow: `0 0 0 4px ${t.pageBg}, 0 0 0 5px ${t.rail}`,
                          fontFamily: "'Instrument Serif', Georgia, serif",
                        }}
                        aria-hidden
                      >
                        {new Date(day).getDate()}
                      </div>
                      <h2
                        className="text-xl sm:text-2xl font-semibold leading-tight"
                        style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, color: t.headingText }}
                      >
                        {heading}
                      </h2>
                      <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${t.divider} 0%, transparent 100%)` }} />
                      <span className="text-[11px] uppercase tracking-widest" style={{ color: t.fadedText }}>
                        {items.length} ship{items.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="sm:pl-12 space-y-3">
                      {items.map(item => (
                        <ReleaseCard key={item.id} item={item} t={t} kindTok={KIND_TOKENS[item.kind]} contribTok={item.contributor ? CONTRIBUTOR_TOKENS[item.contributor] : null} />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          </div>
        )}

        {/* CALENDAR VIEW */}
        {view === 'calendar' && calendar && (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <CalendarIcon className="w-5 h-5" style={{ color: PALETTE.gold }} />
                <h2 className="text-xl sm:text-2xl font-semibold" style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, color: t.headingText }}>
                  {calendar.monthLabel || 'No data'}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setMonthOffset(Math.min(monthOffset + 1, calendar.monthsCount - 1))} disabled={Math.abs(monthOffset) >= calendar.monthsCount - 1} className="p-2 rounded border disabled:opacity-30" style={{ borderColor: t.cardBorder, color: t.headingText }}><ChevronLeft className="w-4 h-4" /></button>
                <button type="button" onClick={() => setMonthOffset(Math.max(monthOffset - 1, 0))} disabled={monthOffset === 0} className="p-2 rounded border disabled:opacity-30" style={{ borderColor: t.cardBorder, color: t.headingText }}><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
            <div
              className="rounded-lg p-3 sm:p-5 mb-12"
              style={{ background: t.cardBg, border: `1px solid ${t.cardBorder}`, boxShadow: t.cardShadow }}
            >
              <div className="grid grid-cols-7 gap-1 mb-2 text-[10px] uppercase tracking-widest" style={{ color: t.fadedText }}>
                {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => <div key={d} className="text-center py-1.5">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
                {calendar.cells.map((cell, i) => {
                  // Daily metrics footer — small pill-row at the bottom of each
                  // day cell showing what we ingested/produced that day. Only
                  // surfaces metrics with non-zero activity to avoid clutter.
                  // Tooltip carries the full breakdown.
                  let dailyKey = '';
                  if (cell.date) {
                    const y = cell.date.getFullYear();
                    const m = String(cell.date.getMonth() + 1).padStart(2, '0');
                    const dd = String(cell.date.getDate()).padStart(2, '0');
                    dailyKey = `${y}-${m}-${dd}`;
                  }
                  const dailyMetric = dailyKey ? dailyByDay.get(dailyKey) : undefined;
                  const hasDailyActivity = dailyMetric && dailyMetric.total > 0;
                  const dailyTooltip = dailyMetric ? [
                    dailyMetric.chargemaster > 0 ? `${dailyMetric.chargemaster.toLocaleString()} pricing rows` : null,
                    dailyMetric.scenarios > 0 ? `${dailyMetric.scenarios} scenarios` : null,
                    dailyMetric.crosswalk > 0 ? `${dailyMetric.crosswalk.toLocaleString()} intl codes mapped` : null,
                    dailyMetric.translations > 0 ? `${dailyMetric.translations.toLocaleString()} translations` : null,
                    dailyMetric.voice > 0 ? `${dailyMetric.voice} voice lines` : null,
                  ].filter(Boolean).join(' · ') : '';

                  return (
                  <div
                    key={i}
                    className="rounded-md min-h-[88px] sm:min-h-[110px] p-2 flex flex-col gap-1"
                    style={{
                      background: cell.date ? (isDark ? 'rgba(0,8,28,0.6)' : '#FFFFFF') : 'transparent',
                      border: cell.date ? `1px solid ${t.divider}` : '1px solid transparent',
                    }}
                  >
                    {cell.date && (
                      <>
                        <div className="text-[10px] font-mono" style={{ color: t.fadedText }}>{cell.date.getDate()}</div>
                        <div className="flex flex-col gap-1 flex-1">
                          {cell.items.slice(0, 3).map(item => {
                            const tok = KIND_TOKENS[item.kind];
                            return (
                              <div
                                key={item.id}
                                className="text-[10px] px-1.5 py-1 rounded truncate"
                                style={{ background: tok.soft, color: tok.pillTx, borderLeft: `2px solid ${tok.stripe}` }}
                                title={item.title}
                              >
                                {item.title}
                              </div>
                            );
                          })}
                          {cell.items.length > 3 && <div className="text-[9px]" style={{ color: t.fadedText }}>+{cell.items.length - 3} more</div>}
                        </div>
                        {hasDailyActivity && (
                          <div
                            className="mt-auto -mx-1 -mb-1 px-1.5 py-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[9px] tabular-nums leading-none rounded-b-sm"
                            style={{
                              background: isDark ? 'rgba(197,165,90,0.06)' : 'rgba(197,165,90,0.08)',
                              borderTop: `1px solid ${t.divider}`,
                              color: t.mutedText,
                            }}
                            title={dailyTooltip}
                          >
                            {dailyMetric!.chargemaster > 0 && (
                              <span style={{ color: '#5B9BD5' }} title={`${dailyMetric!.chargemaster.toLocaleString()} pricing rows`}>
                                <span style={{ opacity: 0.6 }}>$</span>{compactNum(dailyMetric!.chargemaster)}
                              </span>
                            )}
                            {dailyMetric!.scenarios > 0 && (
                              <span style={{ color: '#10B981' }} title={`${dailyMetric!.scenarios} scenarios`}>
                                <span style={{ opacity: 0.6 }}>+</span>{compactNum(dailyMetric!.scenarios)}<span style={{ opacity: 0.6 }}>s</span>
                              </span>
                            )}
                            {dailyMetric!.crosswalk > 0 && (
                              <span style={{ color: '#7BC0E0' }} title={`${dailyMetric!.crosswalk.toLocaleString()} intl codes mapped`}>
                                {compactNum(dailyMetric!.crosswalk)}<span style={{ opacity: 0.6 }}>i</span>
                              </span>
                            )}
                            {dailyMetric!.translations > 0 && (
                              <span style={{ color: '#C5A55A' }} title={`${dailyMetric!.translations.toLocaleString()} translations`}>
                                {compactNum(dailyMetric!.translations)}<span style={{ opacity: 0.6 }}>t</span>
                              </span>
                            )}
                            {dailyMetric!.voice > 0 && (
                              <span style={{ color: '#B22234' }} title={`${dailyMetric!.voice} voice lines`}>
                                {compactNum(dailyMetric!.voice)}<span style={{ opacity: 0.6 }}>v</span>
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Footer */}
        <footer className="mt-12 pt-6 border-t text-xs flex items-center justify-between" style={{ borderColor: t.divider, color: t.fadedText }}>
          <span>This page reflects every public-facing change. Internal infra changes are excluded from public view.</span>
          <Link to="/data-methodology" className="underline" style={{ color: PALETTE.gold }}>How we measure things →</Link>
        </footer>
      </div>
    </div>
  );
};

// ─── COMPONENTS ───────────────────────────────────────────────────────

function StatTile({ t, label, value, sublabel, accent }: { t: any; label: string; value: string; sublabel: string; accent: string }) {
  return (
    <div
      className="relative rounded-lg p-4 pl-5 transition-shadow"
      style={{
        background: t.statTile,
        border: `1px solid ${t.cardBorder}`,
        boxShadow: t.cardShadow,
      }}
    >
      <div
        className="absolute top-3 bottom-3 left-0 w-[3px] rounded-full"
        style={{ background: accent, boxShadow: `0 0 8px ${accent}55` }}
        aria-hidden
      />
      <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: t.fadedText }}>{label}</div>
      <div
        className="text-3xl sm:text-4xl font-bold tabular-nums leading-none mb-1"
        style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, color: t.headingText }}
      >
        {value}
      </div>
      <div className="text-[11px]" style={{ color: t.mutedText }}>{sublabel}</div>
    </div>
  );
}

function ReleaseCard({ item, t, kindTok, contribTok }: { item: ReleaseNote; t: any; kindTok: typeof KIND_TOKENS_DARK[string]; contribTok: typeof CONTRIBUTOR_TOKENS[string] | null }) {
  const Icon = kindTok.icon;
  return (
    <article
      className="relative rounded-lg p-5 pl-6 transition-shadow group"
      style={{
        background: t.cardBg,
        border: `1px solid ${t.cardBorder}`,
        boxShadow: t.cardShadow,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = t.cardHover; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = t.cardShadow; }}
    >
      {/* Flag-stripe left accent — same device as Quality cards */}
      <div
        className="absolute top-3 bottom-3 left-0 w-[3px] rounded-full"
        style={{ background: kindTok.stripe, boxShadow: `0 0 8px ${kindTok.stripe}66` }}
        aria-hidden
      />

      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
            style={{ background: kindTok.pillBg, color: kindTok.pillTx, border: `1px solid ${kindTok.pillBd}` }}
          >
            <Icon className="w-2.5 h-2.5" /> {kindTok.pillLabel}
          </span>
          {contribTok && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium"
              style={{ background: contribTok.bg, color: contribTok.tx }}
            >
              {contribTok.label}
            </span>
          )}
          <span className="text-[10px] tabular-nums" style={{ color: t.fadedText }}>{formatTime(item.shipped_at)}</span>
        </div>
      </div>

      <h3
        className="text-base sm:text-lg font-semibold leading-snug mb-2 tracking-tight"
        style={{ color: t.headingText }}
      >
        {item.title}
      </h3>

      {item.links?.filter(isVideoLink).map((l, i) => (
        <ReleaseVideo key={`v${i}`} src={l.url} poster={l.poster} label={l.label} />
      ))}
      {item.body_md && (
        <MarkdownBody text={item.body_md} color={t.bodyText} />
      )}

      {item.metric && Object.keys(item.metric).length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
          {Object.entries(item.metric).map(([k, v]) => (
            <span key={k} className="text-[11px] tabular-nums" style={{ color: t.mutedText }}>
              <strong style={{ color: t.headingText }}>{typeof v === 'number' ? v.toLocaleString() : String(v)}</strong>{' '}
              <span style={{ color: t.fadedText }}>{k.replace(/_/g, ' ')}</span>
            </span>
          ))}
        </div>
      )}

      {item.links && item.links.filter(l => !isVideoLink(l)).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {item.links.filter(l => !isVideoLink(l)).map(l => (
            <Link
              key={l.url}
              to={l.url}
              className="inline-flex items-center gap-1 text-xs underline-offset-2 hover:underline"
              style={{ color: PALETTE.gold }}
            >
              {l.label} <ExternalLink className="w-3 h-3" />
            </Link>
          ))}
        </div>
      )}
    </article>
  );
}

export default ReleaseNotes;
